import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { trackCtaClick, trackNavClick } from '../lib/analytics';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id, navLabel, location_) => {
    setIsMobileMenuOpen(false);
    if (navLabel) trackNavClick(navLabel, location_);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      // Delay scroll until after navigation renders
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Solutions', id: 'solutions' },
    { label: 'Industries', id: 'industries' },
    { label: 'Integrations', id: 'integrations' },
    { label: 'Pricing', id: 'pricing' },
  ];

  // Native <a href> links only activate on Enter. This restores Space-key
  // activation (matching the previous <button> behaviour) without affecting
  // Enter, which continues to work via the existing onClick handlers.
  const handleSectionKeyDown = (e, action) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      action();
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== '/'
          ? 'bg-[#101F27]/95 backdrop-blur-lg shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
              data-testid="header-logo-link"
            >
              <img
                src="/images/wristcoin_logo.png"
                alt="WristCoin"
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`/#${link.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.id, link.label, 'header_desktop');
                }}
                onKeyDown={(e) =>
                  handleSectionKeyDown(e, () => scrollToSection(link.id, link.label, 'header_desktop'))
                }
                className="text-gray-300 hover:text-[#009688] transition-colors duration-200 text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/#demo"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('demo', 'Contact', 'header_desktop');
              }}
              onKeyDown={(e) =>
                handleSectionKeyDown(e, () => scrollToSection('demo', 'Contact', 'header_desktop'))
              }
              className="text-gray-300 hover:text-[#009688] transition-colors duration-200 text-sm font-medium"
            >
              Contact
            </a>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              asChild
              className="bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white shadow-lg shadow-[#009688]/20"
            >
              <a
                href="https://demo.mywristcoin.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCtaClick('try_it_now', 'header_desktop', { destination: 'https://demo.mywristcoin.com' })}
              >
                Try It Now
              </a>
            </Button>
            <Button
              asChild
              className="bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white shadow-lg shadow-[#009688]/20"
            >
              <a
                href="https://share.hsforms.com/1JU7FumyhQtyEhJbBslXmEgsn91p"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCtaClick('request_demo', 'header_desktop', { destination: 'hubspot_form' })}
              >
                Book a Demo
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-300 hover:text-[#009688] transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#101F27]/98 backdrop-blur-lg border-t border-gray-800">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`/#${link.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.id, link.label, 'header_mobile');
                }}
                onKeyDown={(e) =>
                  handleSectionKeyDown(e, () => scrollToSection(link.id, link.label, 'header_mobile'))
                }
                className="block w-full text-left text-gray-300 hover:text-[#009688] transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 space-y-3">
              <Button
                asChild
                variant="outline"
                className="w-full border-[#009688] text-[#009688] hover:bg-[#009688]/10"
              >
                <a
                  href="/#demo"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('demo', 'Contact', 'header_mobile');
                  }}
                  onKeyDown={(e) =>
                    handleSectionKeyDown(e, () => scrollToSection('demo', 'Contact', 'header_mobile'))
                  }
                >
                  Contact
                </a>
              </Button>
              <Button
                asChild
                className="w-full bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white"
              >
                <a
                  href="https://demo.mywristcoin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackCtaClick('try_it_now', 'header_mobile', { destination: 'https://demo.mywristcoin.com' });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Try It Now
                </a>
              </Button>
              <Button
                asChild
                className="w-full bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white"
              >
                <a
                  href="https://share.hsforms.com/1JU7FumyhQtyEhJbBslXmEgsn91p"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackCtaClick('request_demo', 'header_mobile', { destination: 'hubspot_form' });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Book a Demo
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
